import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchHandbook } from "../lib/api";
import { Search as SearchIcon, ArrowUpRight } from "lucide-react";

export default function Search() {
    const [params, setParams] = useSearchParams();
    const q = params.get("q") || "";
    const [input, setInput] = useState(q);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!q) { setResults([]); return; }
        setLoading(true);
        searchHandbook(q).then(setResults).catch(() => setResults([])).finally(() => setLoading(false));
    }, [q]);

    const submit = (e) => {
        e.preventDefault();
        setParams({ q: input.trim() });
    };

    const highlight = (text) => {
        if (!q) return text;
        const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
        return text.split(re).map((p, i) =>
            re.test(p) ? <mark key={i} className="bscc-highlight">{p}</mark> : <span key={i}>{p}</span>
        );
    };

    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-10 lg:py-14 fade-up" data-testid="search-page">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#E57373] font-semibold mb-3">Search</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "Outfit" }}>Find anything in the handbook</h1>
            <form onSubmit={submit} className="mb-8">
                <div className="relative">
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                        type="text"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        placeholder="Search policies, procedures, fares…"
                        autoFocus
                        className="w-full pl-12 pr-4 py-3.5 text-lg bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-[#2C7A85] focus:border-transparent outline-none"
                        data-testid="search-input-main"
                    />
                </div>
            </form>

            {loading && <div className="text-gray-500">Searching…</div>}
            {!loading && q && results.length === 0 && (
                <div className="text-gray-500 bg-white border border-gray-200 rounded-md p-6" data-testid="search-no-results">
                    No matches for "<strong>{q}</strong>". Try a different word, or ask the Handbook Assistant.
                </div>
            )}

            <div className="space-y-3">
                {results.map(r => (
                    <Link
                        key={r.id}
                        to={`/section/${r.id}?highlight=${encodeURIComponent(q)}`}
                        className="block bg-white border border-gray-200 rounded-lg p-5 hover:border-[#2C7A85] hover:shadow-sm transition-all group"
                        data-testid={`search-result-${r.id}`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-1 font-semibold">{r.category}</div>
                                <div className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: "Outfit" }}>{highlight(r.title)}</div>
                                <div className="text-[15px] text-gray-700 leading-relaxed">{highlight(r.snippet)}</div>
                            </div>
                            <ArrowUpRight className="h-5 w-5 text-gray-400 group-hover:text-[#2C7A85] flex-shrink-0" />
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
